import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { Node, Schema } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection } from '@atlaskit/editor-prosemirror/state';

import { isStreamable } from './partial-stream-parser';

export type RemixResultTarget = {
	collection?: string;
	id: string;
	localId?: string;
	type: 'mediaSingle' | 'extension';
};

function getTarget(node: Node): RemixResultTarget | undefined {
	if (node.type.name === 'mediaSingle' && node.firstChild?.type.name === 'media') {
		const { id, collection, type } = node.firstChild.attrs;
		if (type === 'file' && typeof id === 'string' && id) {
			return { type: 'mediaSingle', id, collection, localId: node.attrs.localId };
		}
	}
	if (node.type.name === 'extension' && node.attrs.extensionKey === 'native-embed:maui') {
		const url = node.attrs.parameters?.macroParams?.url?.value;
		if (typeof url === 'string' && url) {
			return { type: 'extension', id: url, localId: node.attrs.localId };
		}
	}
	return undefined;
}

/** Read identities from the final snapshot without applying its edits a second time. */
export function getRemixResultTargets(toolCalls: string, schema: Schema): RemixResultTarget[] {
	const targets: RemixResultTarget[] = [];
	try {
		const parsed = JSON.parse(toolCalls);
		const commands = Array.isArray(parsed) ? parsed : parsed?.data;
		if (!Array.isArray(commands)) {
			return targets;
		}
		for (const command of commands) {
			if (
				!isStreamable(command?.name) ||
				command?.arguments?.contentType !== 'application/adf+json' ||
				typeof command.arguments.value !== 'string'
			) {
				continue;
			}
			try {
				const doc = schema.nodeFromJSON(JSON.parse(command.arguments.value));
				doc.descendants((node) => {
					const target = getTarget(node);
					if (target) {
						targets.push(target);
						return false;
					}
					return true;
				});
			} catch {
				// A malformed or unsupported result must not prevent stream completion.
			}
		}
	} catch {
		// The existing streaming parser owns validation and error reporting.
	}
	return targets;
}

/** Select the last applied asset in tool-call order, rather than the last asset on the page. */
export const selectLastRemixResult =
	(targets: readonly RemixResultTarget[]): EditorCommand =>
	({ tr }) => {
		for (let index = targets.length - 1; index >= 0; index--) {
			const target = targets[index];
			const matches: { localId?: string; pos: number }[] = [];
			tr.doc.descendants((node, pos) => {
				const candidate = getTarget(node);
				if (
					candidate?.type === target.type &&
					candidate.id === target.id &&
					candidate.collection === target.collection &&
					NodeSelection.isSelectable(node)
				) {
					matches.push({ localId: candidate.localId, pos });
					return false;
				}
				return true;
			});
			// Local IDs may be assigned/repaired on insertion. Asset identity is a safe
			// fallback only when it identifies exactly one node in the current document.
			const match =
				(target.localId && matches.find(({ localId }) => localId === target.localId)) ||
				(matches.length === 1 ? matches[0] : undefined);
			if (match) {
				return tr
					.setSelection(NodeSelection.create(tr.doc, match.pos))
					.setMeta('addToHistory', false);
			}
		}
		return null;
	};
